// File-related TypeScript types
// Aligned with api.json (OpenAPI 3.0) – Document, DocumentsResponse, UploadDocumentsRequest, UploadUrlResponse, DocumentPreviewResponse

// Document from API spec (components.schemas.Document)
export interface Document {
  _id: string;
  name: string;
  type?: string; // MIME type
  size?: string; // File size as string (e.g., "2.5MB")
  createdAt?: string;
}

/**
 * Parse API size string (e.g. "2.5MB", "100KB") to bytes.
 * Used when mapping Document.size to FileMetadata.size.
 */
export function parseSizeToBytes(size: string | undefined): number {
  if (size == null || size === '') return 0;
  const s = String(size).trim().toUpperCase();
  const num = parseFloat(s.replace(/[^\d.]/g, ''));
  if (Number.isNaN(num)) return 0;
  if (s.endsWith('GB')) return Math.round(num * 1024 * 1024 * 1024);
  if (s.endsWith('MB')) return Math.round(num * 1024 * 1024);
  if (s.endsWith('KB')) return Math.round(num * 1024);
  return Math.round(num);
}

// FileMetadata for internal use (compatible with Document)
export interface FileMetadata {
  id: string; // maps to _id
  name: string;
  size: number; // parsed from size string if available
  mimeType: string; // maps to type
  createdAt?: string;
  userId?: string;
  url?: string;
}

// UploadDocumentsRequest from API spec
export interface UploadDocumentsRequest {
  files: Array<{
    name: string;
    type?: string; // MIME type, default: "binary/octet-stream"
    size?: string; // File size for display purposes
  }>;
  /** Tax year for the documents (required by backend). */
  taxYear: number;
}

// Legacy interface for backward compatibility
export interface PresignedUrlRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
}

// UploadUrlResponse from API spec
export interface UploadUrlResponse {
  name?: string; // Original file name
  key: string; // S3 object key
  uploadUrl: string; // Pre-signed S3 upload URL
  _id?: string; // Document ID
}

// Legacy interface for backward compatibility
export interface PresignedUrlResponse {
  uploadUrl: string;
  documentId?: string; // Not in new spec, may need to extract from key
  expiresAt?: string;
  key?: string; // S3 key
  name?: string; // File name
}

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: UploadStatus;
  error?: string;
}

export enum UploadStatus {
  PENDING = 'pending',
  UPLOADING = 'uploading',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

// DocumentsResponse from API spec (components.schemas.DocumentsResponse)
export interface DocumentsResponse {
  files: Document[];
}

// FileListResponse for internal use (compatible with DocumentsResponse)
export interface FileListResponse {
  documents: FileMetadata[]; // maps from files array
  total?: number; // Not in API spec, may need to calculate
  page?: number; // Not in API spec
  limit?: number; // Not in API spec
}

// DocumentPreviewResponse from API spec (components.schemas.DocumentPreviewResponse)
export interface DocumentPreviewResponse {
  _id?: string;
  name?: string;
  url: string; // Pre-signed S3 download URL
}

// FilePreviewResponse for backward compatibility
export interface FilePreviewResponse {
  url: string;
  expiresAt?: string; // Not in new spec
}

// Allowed file types for upload
export const ALLOWED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
